"use client";

import type { WhiteboardMark } from "@/lib/whiteboard/boardState";
import { motion } from "framer-motion";
import React, { createContext, forwardRef, useContext } from "react";
import styles from "./WhiteboardLessonCanvas.module.css";
import { InstantWhiteboardContext } from "./WhiteboardWriteInText";

const LessonPresentation = createContext(false);
export const useLessonPresentation = () => useContext(LessonPresentation);
export function LessonPresentationProvider({ children }: { children: React.ReactNode }) {
  return <LessonPresentation.Provider value={true}>{children}</LessonPresentation.Provider>;
}
export type LessonState = "neutral" | "selected" | "correct" | "incorrect";
export function mergeLessonStates(...states: LessonState[]): LessonState {
  return states.includes("incorrect")
    ? "incorrect"
    : states.includes("correct")
      ? "correct"
      : states.includes("selected")
        ? "selected"
        : "neutral";
}
export function LessonCanvas({
  as: Tag = "div",
  children,
  className = "",
  ...props
}: React.HTMLAttributes<HTMLElement> & { as?: "div" | "section" }) {
  return (
    <LessonPresentation.Provider value={true}>
      <InstantWhiteboardContext.Provider value={true}>
        <Tag {...props} className={`${styles.canvas} ${className}`}>
          {children}
        </Tag>
      </InstantWhiteboardContext.Provider>
    </LessonPresentation.Provider>
  );
}
export const LessonContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { chrome?: boolean }
>(function LessonContent({ chrome = false, className = "", ...props }, ref) {
  return <div {...props} ref={ref} className={`${chrome ? styles.content : ""} ${className}`} />;
});
export function LessonHeading({
  as: Tag = "h2",
  className = "",
  children,
  headingRef,
  framed = true,
  size = "large",
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  as?: "h2" | "h3" | "p" | "div";
  headingRef?: React.Ref<HTMLHeadingElement>;
  framed?: boolean;
  size?: "small" | "normal" | "large";
}) {
  const active = useLessonPresentation();
  const headingClass = `${active ? styles.heading : ""} ${className}`;
  if (Tag === "h2" || Tag === "h3")
    return (
      <Tag
        {...props}
        ref={headingRef}
        data-framed={framed}
        data-size={size}
        className={headingClass}
      >
        {children}
      </Tag>
    );
  return (
    <Tag {...props} data-framed={framed} data-size={size} className={headingClass}>
      {children}
    </Tag>
  );
}
export function LessonHeader({
  eyebrow,
  title,
  description,
  headingRef,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <header className={styles.header}>
      {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
      <LessonHeading
        headingRef={headingRef}
        framed={false}
        tabIndex={-1}
        className={styles.headerTitle}
      >
        {title}
      </LessonHeading>
      {description && <p className={styles.description}>{description}</p>}
    </header>
  );
}
export function LessonGroup({
  as: Tag = "div",
  variant = "task",
  className = "",
  children,
  marked = false,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  as?: "div" | "fieldset" | "p";
  variant?: "task" | "answer" | "panel" | "activity";
  marked?: boolean;
}) {
  const active = useLessonPresentation();
  return (
    <Tag
      {...props}
      data-lesson-group={active ? variant : undefined}
      className={`${active && !marked ? styles.group : ""} ${className}`}
    >
      {children}
    </Tag>
  );
}
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  intent?: "primary" | "secondary" | "chrome";
  state?: LessonState;
  mark?: WhiteboardMark;
  textTone?: "normal" | "muted" | "accent";
};
export const LessonButton = forwardRef<HTMLButtonElement, ButtonProps>(function LessonButton(
  {
    intent = "secondary",
    state,
    mark,
    textTone = "normal",
    className = "",
    type = "button",
    ...props
  },
  ref
) {
  const active = useLessonPresentation();
  const selected = props["aria-pressed"] === true || props["aria-pressed"] === "true";
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      data-lesson-control={active ? "button" : undefined}
      data-intent={active ? intent : undefined}
      data-state={active ? (state ?? (selected ? "selected" : "neutral")) : undefined}
      data-mark={active ? mark?.style : undefined}
      data-text-tone={active ? textTone : undefined}
      className={`${active ? styles.button : ""} ${className}`}
    />
  );
});
export const MotionLessonButton = motion.create(LessonButton);
export const LessonInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function LessonInput({ className = "", ...props }, ref) {
  const active = useLessonPresentation();
  return (
    <input
      {...props}
      ref={ref}
      data-lesson-control={active ? "input" : undefined}
      className={`${active ? styles.input : ""} ${className}`}
    />
  );
});
export function LessonFeedback({
  tone = "muted",
  as: Tag = "p",
  className = "",
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  tone?: "muted" | "hint" | "correct" | "incorrect";
  as?: "p" | "div";
}) {
  const active = useLessonPresentation();
  return (
    <Tag
      {...props}
      data-tone={active ? tone : undefined}
      className={`${active ? styles.feedback : ""} ${className}`}
    />
  );
}
